import { computed } from 'vue'

import { NON_CLONABLE_FIELD_NAMES } from '@/constants'

import { useRecord } from './useRecord'

export function useRecordClone({
  id,
  appName,
  modelName,
  annotationTypes,
  keepAttributes = true,
  keepAnnotations = false,
  keepPermissions = false,
  setParent = false,
  nonClonableFieldNames = [],
}) {
  const { record: original, name: cloneName } = useRecord({
    appName,
    modelName,
    id,
  })

  // Annotation type IDs present in the source record
  const annotationKeySet = computed(() => {
    const keys = new Set()
    for (const at of Object.values(annotationTypes.value)) {
      if (at.id in original.value) keys.add(at.id)
    }
    return keys
  })

  const keptAnnotationKeys = computed(() => {
    if (!keepAnnotations) return []
    return Object.values(annotationTypes.value)
      .filter((at) => at.id in original.value && !at.readonly)
      .map((at) => at.id)
  })

  const allNonClonable = new Set([
    ...NON_CLONABLE_FIELD_NAMES,
    ...nonClonableFieldNames,
  ])

  const clonedRecord = computed(() => {
    const result = {}

    if (keepAttributes) {
      for (const [k, v] of Object.entries(original.value)) {
        if (annotationKeySet.value.has(k)) continue
        if (allNonClonable.has(k)) continue
        if (v?.readonly) continue
        result[k] = v
      }
    }

    if (keepPermissions && original.value.permissions) {
      result.permissions = original.value.permissions
    }

    for (const k of keptAnnotationKeys.value) {
      result[k] = original.value[k]
    }

    if (setParent) {
      result.parent = {
        value: {
          app_name: appName,
          model_name: modelName,
          model_type: modelName,
          id,
          name: cloneName.value,
        },
      }
    }

    return result
  })

  return { keptAnnotationKeys, clonedRecord }
}
